/**
 * A document as a page (/d/:vaultId/:uuid[?day=]): what "Open full" in a peek and shared links use. The page is the
 * document peek's content (what it is, the run that stored it, its fields and the payload) laid out full width on
 * one stage-level surface inside the shell; the run that stored it opens beside it in the peek, as on the stage.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useEnv } from '../lib/envs.tsx';
import { rememberSearch } from '../lib/hooks.ts';
import { Shell } from '../shell/Shell.tsx';
import { NounTag, Surface } from '../design/primitives.tsx';
import { Peek, PeekLayout, usePeek } from '../peek/Peek.tsx';
import { DocumentPeek } from '../peek/DocumentPeek.tsx';

export function DocumentPage() {
  const { vaultId = '', uuid = '' } = useParams();
  const [params] = useSearchParams();
  const day = params.get('day') ?? undefined;
  const navigate = useNavigate();
  const peek = usePeek();
  const { link } = useEnv();
  const [copied, setCopied] = useState(false);

  const ref = useMemo(() => (day ? { vaultId, uuid, day } : { vaultId, uuid }), [vaultId, uuid, day]);
  // No time window on a page of its own: a run opened from here is read whole
  const time = useMemo(() => new URLSearchParams(), []);

  const go = (q: string) => {
    if (!q.trim()) return;
    rememberSearch(q);
    void navigate(link(`/s?${new URLSearchParams({ q })}`));
  };
  const copy = () => {
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => undefined);
  };

  return (
    <Shell search={{ value: '', onSubmit: go, size: 'compact' }}>
      <PeekLayout open={peek.stack.length > 0} peek={<Peek time={time} />}>
        <div className="w-full px-6 pt-5 pb-16">
          <div className="flex items-center gap-3 min-w-0">
            <NounTag kind="document" />
            <span className="flex-1" />
            <button type="button" onClick={copy} aria-live="polite"
              className="h-7 px-2.5 rounded-full bg-transparent border-0 cursor-pointer text-small text-n8 whitespace-nowrap transition-colors duration-[var(--t-micro)] hover:bg-n3 hover:text-n10">
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
          <Surface radius="stage" className="mt-3 px-6 pt-4 pb-8 sm:px-8">
            <DocumentPeek key={`${vaultId}/${uuid}/${day ?? ''}`} ref={ref} onRun={(executionId) => peek.open({ kind: 'run', executionId })} />
          </Surface>
        </div>
      </PeekLayout>
    </Shell>
  );
}
